import { SENDA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { DIRS, hexDist, hexToWorld, type Hex } from '../hex.ts';
import type { CairnRules, EchoRules, FallenRules } from '../aster-types.ts';
import type { CairnLayout, EchoLayout, FallenLayout } from './aster-layout.ts';

/**
 * The ranger's chapter 1 rules from their layouts (docs/sendas/explorador.md, «Las piedras de Aster»): the fight's
 * builder (campaign/sendas.ts) and the renderer's scenes build them the same way. SENDA.aster. `radius`: the board's
 * (the layout's + 2: its outskirts round it), so cell ids land on the board the fight walks.
 */

const at = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);

export const cairnRules = (L: CairnLayout, radius: number): CairnRules => {
  const C = SENDA.aster.cairns;
  return {
    cairns: L.cairns.map((c, i) => ({ ...at(c), cell: cellId(radius, c.q, c.r), plate: !!L.plates[i] })),
    way: [...L.way],
    knots: L.knots.map((k) => ({ ...k })),
    read: C.read,
    reach: C.reach,
    sling: { ...C.sling },
    crows: { ...C.crows },
    wrong: { ...C.wrong },
    rim: L.rim.map(at),
  };
};

export const echoRules = (L: EchoLayout, radius: number): EchoRules => {
  const E = SENDA.aster.echo;
  return {
    forks: L.forks.map((f) => ({
      at: at(f.at),
      cell: cellId(radius, f.at.q, f.at.r),
      mouths: f.mouths.map((m) => {
        // Its far end: the cell furthest from the fork (a dead end's wall, or the next fork).
        let end = m.cells[0] as Hex;
        let far = -1;
        for (const c of m.cells) {
          const d = hexDist(c, f.at);
          if (d > far) {
            far = d;
            end = c;
          }
        }
        const d = DIRS[m.dir] as Hex;
        const rim = { q: f.at.q + d.q, r: f.at.r + d.r };
        return { at: at(rim), end: at(end), cells: m.cells.map((c) => cellId(radius, c.q, c.r)).sort((a, b) => a - b), open: m.open, wide: m.wide };
      }),
    })),
    exit: at(L.exit),
    speed: E.speed,
    far: E.far,
    cool: E.cool,
    sight: E.sight,
    crows: { ...E.crows },
    reach: E.reach,
  };
};

export const fallenRules = (L: FallenLayout, radius: number, rollerHp: number): FallenRules => {
  const F = SENDA.aster.fallen;
  const R = F.roller;
  return {
    cairns: L.cairns.map((c) => ({ ...at(c), cell: cellId(radius, c.q, c.r), plate: false })),
    long: L.long,
    cord: { ...L.cord },
    named: L.named,
    bases: L.bases.map((b) => ({ ...at(b), cell: cellId(radius, b.q, b.r) })),
    truth: L.truth,
    obvious: L.obvious,
    read: F.read,
    reach: F.reach,
    stand: F.stand,
    sling: { ...F.sling },
    crows: { ...F.crows },
    wrong: { ...F.wrong },
    rim: L.rim.map(at),
    roller: {
      hp: rollerHp,
      at: R.at,
      pace: R.pace,
      bite: { ...R.bite },
      every: R.every,
      warn: R.warn,
      speed: R.speed,
      len: R.len,
      r: R.r,
      heroBp: R.heroBp,
      push: R.push,
      stun: R.stun,
      rest: R.rest,
      from: { ...L.from },
    },
  };
};
